/**
 * ChartSelector Component Styles
 */

.container {
  padding: var(--spacing-lg);
  max-width: 500px;
  margin: 0 auto;
  min-height: 100vh;
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: var(--spacing-xs);
}

.title {
  font-size: var(--font-size-xxl);
  text-align: center;
  padding-right: 76px;  /* Space for action buttons */
  padding-left: 76px;   /* Symmetric padding for centering */
}

.headerActions {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
}

.headerBtn {
  padding: var(--spacing-xs);
  color: var(--tg-theme-hint-color);
  background: none;
  border-radius: var(--border-radius-sm);
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.headerBtn:active {
  color: var(--tg-theme-text-color);
}

.settingsButton {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: var(--spacing-xs);
  color: var(--tg-theme-hint-color);
  background: none;
  border-radius: var(--border-radius-sm);
  transition: color 0.2s ease;
}

.settingsButton:active {
  color: var(--tg-theme-text-color);
}

.subtitle {
  text-align: center;
  color: var(--tg-theme-hint-color);
  margin-bottom: var(--spacing-xl);
}

/* Chart List */
.chartList {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

/* Chart Card Wrapper - contains card and delete button */
.chartCardWrapper {
  position: relative;
  overflow: hidden;
  border-radius: var(--border-radius);
}

/* Delete Button - positioned behind the card */
.deleteButton {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: #fff;
  background-color: var(--tg-theme-destructive-text-color, #ff3b30);
  border: none;
  cursor: pointer;
}

.deleteButton:active {
  opacity: 0.8;
}

/* Chart Card */
.chartCard {
  position: relative;
  width: 100%;
  padding: var(--spacing-md);
  text-align: left;
  background-color: var(--tg-theme-secondary-bg-color);
  border-radius: var(--border-radius);
  transition: transform 0.25s ease;
  z-index: 1;
}

.chartCard.swiped {
  transform: translateX(-80px);
}

.chartCard:active:not(.swiped) {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Delete icon for desktop hover */
.deleteIcon {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tg-theme-destructive-text-color, #ff3b30);
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  opacity: 0;
  animation: fadeIn 0.15s ease forwards;
  z-index: 2;
  transition: transform 0.15s ease, background 0.15s ease;
}

.deleteIcon:hover {
  transform: scale(1.1);
  background: #ff2d20;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

/* Hide delete icon on touch devices */
@media (hover: none) and (pointer: coarse) {
  .deleteIcon {
    display: none;
  }
}

.primaryBadge {
  position: absolute;
  top: var(--spacing-sm);
  right: var(--spacing-sm);
  padding: 2px var(--spacing-sm);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--tg-theme-button-text-color);
  background-color: var(--tg-theme-accent-text-color);
  border-radius: var(--border-radius-sm);
  z-index: 1;
}

.chartHeader {
  margin-bottom: var(--spacing-sm);
}

.chartName {
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
  padding-right: 70px;
}

.signs {
  display: flex;
  gap: var(--spacing-md);
  font-size: var(--font-size-md);
}

.sunSign {
  color: #f59e0b;
}

.moonSign {
  color: var(--tg-theme-hint-color);
}

/* Chart Details */
.chartDetails {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding-top: var(--spacing-sm);
  border-top: 1px solid rgba(128, 128, 128, 0.2);
}

.detailRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detailLabel {
  font-size: var(--font-size-sm);
  color: var(--tg-theme-hint-color);
}

.detailValue {
  font-size: var(--font-size-sm);
  color: var(--tg-theme-text-color);
}

/* Empty State */
.emptyState {
  padding: var(--spacing-xl);
  text-align: center;
}

.emptyText {
  font-size: var(--font-size-lg);
  color: var(--tg-theme-text-color);
  margin-bottom: var(--spacing-sm);
}

.emptyHint {
  font-size: var(--font-size-sm);
  color: var(--tg-theme-hint-color);
}

/* Loading State */
.loadingContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: var(--spacing-md);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--tg-theme-secondary-bg-color);
  border-top-color: var(--tg-theme-button-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error State */
.errorContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: var(--spacing-md);
  text-align: center;
}

.errorText {
  color: var(--tg-theme-destructive-text-color);
  font-size: var(--font-size-md);
}

.retryButton {
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--font-size-md);
  color: var(--tg-theme-button-text-color);
  background-color: var(--tg-theme-button-color);
  border-radius: var(--border-radius-sm);
}
